import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../axiosConfig';
import { useLocation } from 'react-router-dom';
import '../DocumentUploadWithToken.css';


function DocumentUploadWithToken() {
  const location = useLocation();
  const [token, setToken] = useState('');
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(
    localStorage.getItem('selectedDepartment')
  );
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);

  const selectedBranch = localStorage.getItem('selectedBranch');
  const selectedBranchId = Number(localStorage.getItem('selectedBranchId'));

  const queryToken = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token') || '';
  }, [location.search]);

  useEffect(() => {
    setToken(queryToken);
  }, [queryToken]);

  useEffect(() => {
    // Load document types so we can render the same upload UI.
    // Backend behavior in Documents.js: it loads /document-types/ and then filters by department.
    axiosInstance
      .get('/document-types/')
      .then((res) => {
        const types = Array.isArray(res.data) ? res.data : [];
        setDocumentTypes(types);
      })
      .catch((err) => {
        console.error('Failed to fetch document types:', err);
        setDocumentTypes([]);
        setMessage('Error loading document types.');
      });
  }, []);

  const filteredDocumentTypes = useMemo(() => {
    if (!Array.isArray(documentTypes)) return [];

    // If department is not available (common when user lands from an invite link),
    // show all document types instead of blocking the UI forever.
    if (!selectedDepartment) return documentTypes;

    return documentTypes.filter((doc) => {
      const dep = doc?.department;

      // Support both array and string shapes.
      if (Array.isArray(dep)) return dep.includes(selectedDepartment);
      if (typeof dep === 'string') return dep.includes(selectedDepartment);

      return false;
    });
  }, [documentTypes, selectedDepartment]);

  const groupByCategory = (docs) => {
    const grouped = {};
    Object.keys(docs).forEach(() => {});
    return docs.reduce((acc, doc) => {
      const category = doc.category || 'Others';
      if (!acc[category]) acc[category] = [];
      acc[category].push(doc);
      return acc;
    }, {});
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!token) {
      setMessage('Invalid or missing token.');
      return;
    }

    const formData = new FormData();
    formData.append('token', token);

    const fileInputs = document.querySelectorAll('input[name="file"]');
    const documentTypeInputs = document.querySelectorAll('input[name="document_type_id"]');
    const statusSelects = document.querySelectorAll('select[name="legend_status_display"]');

    // Build files and document_types in a single consistent 1:1 loop.
    // We must NOT rely on separate querySelectorAll(...) arrays alignment.

    const pairs = [];
    const items = Array.from(document.querySelectorAll('.document-item'));

    for (const itemEl of items) {
      const typeEl = itemEl.querySelector('input[name="document_type_id"]');
      const fileEl = itemEl.querySelector('input[name="file"]');

      if (!typeEl || !fileEl) continue;

      const docTypeId = typeEl.value;
      const docLabel = typeEl.getAttribute('data-label');
      const file = fileEl.files?.[0];

      if (!file || !docTypeId) continue;

      pairs.push({ file, docTypeId, docLabel, itemEl });
    }

    let pickedFilesCount = 0;

    for (const p of pairs) {
      pickedFilesCount += 1;
    
      formData.append('files', p.file);
      // Backend: request.data.getlist('document_types')
      formData.append('document_types', p.docTypeId);
    
      if (p.docLabel === 'Criminal Record') {
        const selectEl = p.itemEl.querySelector(
          'select[name="legend_status_display"]'
        );
    
        const selectedStatus = selectEl?.value || '';
    
        formData.append(
          'legend_status_display',
          selectedStatus
        );
      }
    }


    if (pickedFilesCount === 0) {
      setMessage('No files uploaded. Please choose at least one file.');
      return;
    }

    try {
      setUploading(true);
      setMessage('');

      // Use backend token upload endpoint (payload uses files[] + document_types[])
      await axiosInstance.post('/documents/upload-with-token/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setMessage('Files uploaded successfully.');
    } catch (error) {
      console.error(error);
      setMessage('Error uploading files.');
    } finally {
      setUploading(false);
    }
  };

  if (!token) {
    return (
      <div className="dashboard-wrapper" style={{ padding: 20 }}>
        <h2>Invalid upload link</h2>
        <p>Missing token in URL.</p>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper" style={{ padding: 20 }}>
      <div className="token-upload-header">
        <div className="doc">
          <h2>Upload Documents</h2>
        </div>
        <p className="token-upload-subtitle">
          Token is valid for 24 Hours.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="update-documents token-upload-form">
        <div className="update-documents-body">
          {filteredDocumentTypes.length === 0 ? (
            <p className="loading">Loading document types…</p>
          ) : (
            Object.entries(groupByCategory(filteredDocumentTypes)).map(
              ([category, docs]) => (
                <fieldset key={category} className="document-section">
                  <legend className="document-section-title">
                    {category || 'Others'}
                  </legend>

                  <div className="document-list">
                    {[...docs]
                      .filter((doc) => doc.name !== 'KYC Verification')
                      .map((doc) => (
                        <div key={doc.id} className="document-item">
                          <label className="document-label">{doc.name}</label>

                          {doc.name === 'Offer Letter' ||
                          doc.name === 'Appointment Letter' ? (
                            <input
                              type="file"
                              name="file"
                              disabled
                              className="document-input disabled-input"
                            />
                          ) : (
                            <input
                              type="file"
                              name="file"
                              className="document-input"
                            />
                          )}

                          <input
                            type="hidden"
                            name="document_type_id"
                            value={doc.id}
                            data-label={doc.name}
                          />

                          {doc.name === 'Criminal Record' && (
                            <select
                              name="legend_status_display"
                              required
                              className="document-select"
                              defaultValue=""
                            >
                              <option value="">-- Select Status --</option>
                              <option value="✓ Clear">✓ Clear</option>
                              <option value="⚠ Minor Discrepancy">
                                ⚠ Minor Discrepancy
                              </option>
                              <option value="✘ Major Discrepancy">
                                ✘ Major Discrepancy
                              </option>
                              <option value="ℹ Unable To Verify">
                                ℹ Unable To Verify
                              </option>
                            </select>
                          )}
                        </div>
                      ))}
                  </div>
                </fieldset>
              )
            )
          )}
        </div>

        <div className="update-documents-footer">
        {message && (
          <p className="update-documents-message" style={{ color: '#b00020' }}>
            {message}
          </p>
        )}
          <button
            type="submit"
            className="submit-all-btn"
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : 'Submit'}
          </button>
          
        </div>
      </form>
    </div>
  );
}

export default DocumentUploadWithToken;

